/* eslint-disable react/jsx-indent */
// jsx-indent disabled (item 224): the filter MenuLists are wrapped in <Portal> (fixes the
// see-through menu inside the glass panel's blur container, same as item 97); the menu
// contents keep their original indent for a readable diff.
import React from 'react';
import {
  Box, Button, Flex, Menu, MenuButton, MenuItemOption, MenuList, MenuOptionGroup, Portal,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { schemeFor, humanizeStatus } from '../theme/statusColor';
import { fiscalYearRange } from '../models/companyFiscal';
import { isArchivedRow } from '../utils/archived';

// The shared filter bar above every document grid (items 70 + 71). Today it holds
// the status filter; item 71 adds a date-range control to the same bar. Filtering
// is client-side over the loaded list (the grids already load the full list) — for
// very large lists this should move to a backend `status=` / `from=&to=` query.

export interface StatusFilterConfig {
  // the doc type's status label fn (same one the status column uses)
  statusLabel: (value: any) => string;
  // the numeric enum values for this doc type, in display order
  values: number[];
  // when true, offer an "Archived" bucket (the grid must supply archived rows)
  showArchived?: boolean;
}

// 'all' = active only (not void/archived), a number = that status,
// 'void' = voided bucket, 'archived' = archived bucket
export type StatusFilterValue = 'all' | 'void' | 'archived' | number;

// the numeric members of a TS string-enum object, in declaration order
export const enumNumericValues = (e: Record<string, any>): number[] => Object.values(e).filter((v) => typeof v === 'number') as number[];

// ---- date-range filter (item 71) -------------------------------------------
// Quick presets over a from/to window. 'year' = the current FISCAL year (item 68):
// the window is computed from the company's fiscal-year start month so the grid
// preset stays consistent with the dashboard aggregates. Month/quarter stay calendar.
export type DateFilterValue = 'all' | 'month' | 'quarter' | 'year';

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// inclusive [from, to] yyyy-MM-dd window for a preset, or null for 'all'.
// yearStartMonth (1..12) scopes 'year' to the fiscal year (default 1 = calendar).
export const dateRangeFor = (
  value: DateFilterValue,
  yearStartMonth = 1,
  now: Date = new Date(),
): { from: string, to: string } | null => {
  const y = now.getFullYear();
  const m = now.getMonth();
  if (value === 'month') return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
  if (value === 'quarter') {
    const qStart = Math.floor(m / 3) * 3;
    return { from: ymd(new Date(y, qStart, 1)), to: ymd(new Date(y, qStart + 3, 0)) };
  }
  if (value === 'year') return fiscalYearRange(yearStartMonth, now);
  return null;
};

export const dateFilterLabels: Record<DateFilterValue, string> = {
  all: 'All time',
  month: 'This month',
  quarter: 'This quarter',
  year: 'This year',
};

// filter rows whose `field` (a date-only or ISO string) falls in the preset window
export const applyDateFilter = <T extends Record<string, any>>(
  rows: T[],
  value: DateFilterValue,
  field: string,
  yearStartMonth = 1,
): T[] => {
  const range = dateRangeFor(value, yearStartMonth);
  if (!range) return rows;
  return rows.filter((r) => {
    const raw = r[field];
    if (!raw) return false;
    const day = String(raw).slice(0, 10); // yyyy-MM-dd (handles "...T00:00:00")
    return day >= range.from && day <= range.to;
  });
};

// Apply the chosen status filter to a row set (exported so the shell can reuse it
// and keep its record count in sync).
export const applyStatusFilter = <T extends {
  status?: any, isVoid?: boolean, isArchive?: boolean, isArchived?: boolean,
}>(
    rows: T[],
    value: StatusFilterValue,
  ): T[] => {
  // active = the default "All" view: voided + archived live in their own buckets.
  // item 714 — archived may arrive as isArchive OR isArchived (per DTO); test both.
  const arch = (r: T) => isArchivedRow(r);
  if (value === 'all') return rows.filter((r) => !r.isVoid && !arch(r));
  if (value === 'void') return rows.filter((r) => r.isVoid);
  if (value === 'archived') return rows.filter((r) => arch(r));
  return rows.filter((r) => !r.isVoid && !arch(r) && r.status === value);
};

const Dot: React.FC<{ scheme: string }> = ({ scheme }) => (
  <Box as="span" display="inline-block" w="8px" h="8px" borderRadius="full" bg={`${scheme}.400`} mr={2} />
);

interface DocumentFilterBarProps {
  statusFilter?: StatusFilterConfig;
  statusValue: StatusFilterValue;
  onStatusChange: (value: StatusFilterValue) => void;
  // when true, also render the date-range preset dropdown (item 71)
  showDateFilter?: boolean;
  dateValue: DateFilterValue;
  onDateChange: (value: DateFilterValue) => void;
}

const DocumentFilterBar: React.FC<DocumentFilterBarProps> = ({
  statusFilter, statusValue, onStatusChange, showDateFilter, dateValue, onDateChange,
}) => {
  const barBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  // item 224 — the filter menus render INSIDE the blur-glass grid surface and inherit its
  // transparency (same root cause as item 97's kebab). Portal lifts them to the document
  // root + a solid bg/zIndex makes them opaque so the grid no longer bleeds through.
  const menuBg = useColorModeValue('white', 'gray.800');
  if (!statusFilter && !showDateFilter) return null;

  const labelFor = (v: StatusFilterValue): string => {
    if (v === 'all') return 'All statuses';
    if (v === 'void') return 'Voided';
    if (v === 'archived') return 'Archived';
    return statusFilter ? humanizeStatus(statusFilter.statusLabel(v)) : String(v);
  };

  return (
    <Flex mb={4} gap={3} align="center" wrap="wrap">
      {statusFilter && (
        <Menu closeOnSelect>
          <MenuButton
            as={Button}
            size="sm"
            variant="outline"
            borderColor={barBorder}
            rightIcon={<ChevronDownIcon />}
            fontWeight="semibold"
          >
            {`Status: ${labelFor(statusValue)}`}
          </MenuButton>
          <Portal>
          <MenuList minW="220px" maxH="60vh" overflowY="auto" bg={menuBg} zIndex={1500} boxShadow="lg">
            <MenuOptionGroup
              type="radio"
              value={String(statusValue)}
              onChange={(v) => {
                const val = v as string;
                if (val === 'all' || val === 'void' || val === 'archived') onStatusChange(val);
                else onStatusChange(Number(val));
              }}
            >
              <MenuItemOption value="all" fontSize="sm">All statuses</MenuItemOption>
              {statusFilter.values.map((v) => (
                <MenuItemOption key={v} value={String(v)} fontSize="sm">
                  <Dot scheme={schemeFor(statusFilter.statusLabel(v))} />
                  {humanizeStatus(statusFilter.statusLabel(v))}
                </MenuItemOption>
              ))}
              <MenuItemOption value="void" fontSize="sm">
                <Dot scheme={schemeFor(undefined, true)} />
                Voided
              </MenuItemOption>
              {statusFilter.showArchived && (
                <MenuItemOption value="archived" fontSize="sm">
                  <Dot scheme="gray" />
                  Archived
                </MenuItemOption>
              )}
            </MenuOptionGroup>
          </MenuList>
          </Portal>
        </Menu>
      )}

      {showDateFilter && (
        <Menu closeOnSelect>
          <MenuButton
            as={Button}
            size="sm"
            variant="outline"
            borderColor={barBorder}
            rightIcon={<ChevronDownIcon />}
            fontWeight="semibold"
          >
            {`Date: ${dateFilterLabels[dateValue]}`}
          </MenuButton>
          <Portal>
          <MenuList minW="200px" bg={menuBg} zIndex={1500} boxShadow="lg">
            <MenuOptionGroup
              type="radio"
              value={dateValue}
              onChange={(v) => onDateChange(v as DateFilterValue)}
            >
              {(Object.keys(dateFilterLabels) as DateFilterValue[]).map((k) => (
                <MenuItemOption key={k} value={k} fontSize="sm">{dateFilterLabels[k]}</MenuItemOption>
              ))}
            </MenuOptionGroup>
          </MenuList>
          </Portal>
        </Menu>
      )}
    </Flex>
  );
};

DocumentFilterBar.defaultProps = {
  statusFilter: undefined,
  showDateFilter: false,
};

export default DocumentFilterBar;
